//-----------CORE-----------//
import React, { useState, Fragment, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { useFormik } from "formik";
//-----------MUI-----------//
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { makeStyles } from "@mui/styles";
//-----------MUI ICONS-----------//
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
//-----------EXTERNAL-----------//
import { setFilter, resetFilter } from "rtkSlices/filterDevicesSlice";
import { setMapPage, setListPage } from "rtkSlices/AssetViewSlice";
import Groups from "components/Groups";
import Pin from "assets/img/location-pin.png";

const useStyles = makeStyles({
  assetCard: {
    borderRadius: "15px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "5px 10px",
    height: "max-content",
    width: "max-content",
    minWidth: "max-content",
    minHeight: "max-content",
    cursor: "pointer",
    // border: "2px solid",
    marginRight: "20px",
    "&:hover": {
      backgroundColor: "#eeeeee",
    },
    "&:active": {
      backgroundColor: "white",
    },
  },
});

export default function Filter(props) {
  let token = window.localStorage.getItem("token");
  const dispatch = useDispatch();
  const styles = useStyles();
  const viewState = useSelector((state) => state.assetView.view);
  const filtersValue = useSelector((state) => state.filterDevice);
  const metaDataValue = useSelector((state) => state.metaData);
  const handleChange = (event, newValue) => {
    dispatch(setFilter({ view: newValue }));
  };
  const [allMeta, setAllMeta] = useState(
    metaDataValue.services.find((s) => s.id == props.id)?.metaTags
  );
  const service = metaDataValue.services.find(s=>s.id == props.id)
  const [alarms, setAlarms] = useState(filtersValue.percist.alarms);
  const [connection, setConnection] = useState(filtersValue.percist.connection);
  const [assetTypes, setAssetTypes] = useState(filtersValue.percist.assetTypes)
  const [group, setGroup] = useState(filtersValue.group);
  const [path, setPath] = React.useState(
    filtersValue.expanded && filtersValue.expanded.length
      ? filtersValue.expanded
      : ["0:All assets"]
  )

  const filterForm = useFormik({
    initialValues: {
      sensorData: filtersValue.percist.measurement.parameter,
      operator: filtersValue.percist.measurement.operation,
      condition: filtersValue.percist.measurement.value,
      metaValue: filtersValue.percist.metaTags.value,
      metaKey: filtersValue.percist.metaTags.key,
    },
  });

  const { t } = useTranslation();

  function handleConnection(status) {
    // if (status == "AVAILABLE") {
    //   if (connection == "" || connection == "UNAVAILABLE")
    //     setConnection(status);
    //   else setConnection("");
    // } else {
    //   if (connection == "" || connection == "AVAILABLE") setConnection(status);
    //   else setConnection("");
    // }
    setConnection(connection == status ? "" : status);
  }

  function handleAlarms(severity) {
    let old = [...alarms];
    if (alarms.indexOf(severity) == -1) {
      old.push(severity);
    } else {
      old.splice(alarms.indexOf(severity), 1);
    }
    setAlarms(old);
  }

  function isSelected(id){
    if(assetTypes !== null){
      if(assetTypes.includes(id)){
        return true
      } else {
        return false
      }
    } else {
      return true
    }
  }

  function generateSensorsList(){
    // PHASE-B-1 defensive guard. The metadata service lookup at line
    // 68 (`metaDataValue.services.find(s => s.id == props.id)`) can
    // return undefined if the user lands on this drawer after the
    // backing service was deleted, after a service-id refresh race,
    // or in any future host that mounts <Settings> with a transiently
    // missing service. Reading `service.assetMapping` then threw
    // TypeError synchronously inside Groups.handleSelect's
    // onTreeNodeCommit callback, escaping React via MUI TreeView's
    // onNodeSelect.
    //
    // PHB-1 audit followup: returning `[]` was the original guard
    // shape; that turned out to be wrong because the call site at
    // L177 reads it as `sensors: ... generateSensorsList() : null`
    // and downstream consumers (Trend Monitor at L107-113) test
    // `filtersValue.sensors ? filtersValue.sensors[0].name : null`.
    // An empty array is TRUTHY, so the consumer falls into the
    // truthy branch and dereferences `undefined.name`. Returning
    // `null` matches the other-branch shape exactly and leaves the
    // downstream consumer to fall through to its own null-handling.
    if (!service?.assetMapping?.length) return null
    let sensors = []
    assetTypes.forEach((id) => {
      const assetMap = service.assetMapping.find((am) => am?.assetType?._id == id)
      if(assetMap){
        sensors.push(...assetMap.sensors)
      }
    })
    const uniqueSensors = sensors.reduce((accumulator, current) => {
    if (!accumulator.find((item) => item._id === current._id)) {
      accumulator.push(current);
    }
      return accumulator;
    }, []);
    // PHB-1-RESIDUAL-1 (turn-7 audit): if none of the picked
    // assetTypes match anything in service.assetMapping, the reduce
    // collapses to []. Returning [] here recreates the same truthy-
    // empty-array hazard PHB-1 set out to close (filtersValue.sensors
    // is [] is truthy, downstream consumers like Trend Monitor read
    // sensors[0].name → TypeError). Normalise empty result to null
    // so the consumer's falsy-check path runs.
    return uniqueSensors.length ? uniqueSensors : null
  }

  // Derive the full `filterDevice` body from the current local drawer
  // state. Single source of truth for both:
  //   - applyFn (the explicit Apply button — closes the drawer)
  //   - onTreeNodeCommit (the implicit tree-click commit — drawer stays
  //     open so the user can continue interacting with chips/form)
  //
  // Both paths now write the SAME body shape. Pre-PARITY-2-fix, the
  // tree-click commit branch (in components/Groups/index.js) only
  // dispatched `setFilter({ group: next })`, dropping every other local
  // drawer edit (alarms/connection/assetTypes/measurement/metaTags) on
  // the floor: a user who ticked CRITICAL, then clicked a group node,
  // then closed via backdrop would see the group commit but the alarm
  // chip silently revert on next drawer open. Routing tree-click
  // through buildFilterBody+onTreeNodeCommit makes the implicit commit
  // commit ALL local drawer state at the time of click.
  function buildFilterBody(opts = {}) {
    const groupValue = opts.group !== undefined ? opts.group : group;
    const body = {
      group: groupValue,
      alarms: alarms.length > 0 ? JSON.stringify(alarms) : "",
      assetTypes: assetTypes && assetTypes.length ? JSON.stringify(assetTypes) : null,
      sensors: assetTypes && assetTypes.length ? generateSensorsList() : null,
      connection: connection,
      measurement:
        filterForm.values.sensorData != "" &&
        filterForm.values.operator != "" &&
        `${filterForm.values.condition}` != ""
          ? JSON.stringify({
              parameter: filterForm.values.sensorData,
              operation: filterForm.values.operator,
              value: filterForm.values.condition,
            })
          : "",
      metaTags:
        filterForm.values.metaKey != "" && filterForm.values.metaValue != ""
          ? JSON.stringify({
              key: filterForm.values.metaKey,
              value: filterForm.values.metaValue,
            })
          : "",
      percist: {
        alarms: alarms,
        connection: connection,
        assetTypes: assetTypes,
        metaTags: {
          key: filterForm.values.metaKey,
          value: filterForm.values.metaValue,
        },
        measurement: {
          parameter: filterForm.values.sensorData,
          operation: filterForm.values.operator,
          value: filterForm.values.condition,
        },
      },
    };
    // Persist tree-expansion state only on the Apply path. The tree-click
    // path runs INSIDE Groups.handleSelect, BEFORE the props.setPath
    // call at the bottom of handleSelect has flushed through React's
    // batched setState — so the local `path` closure read by this
    // function is one click stale. If we dispatched `expanded: path`
    // on the tree-click commit, Redux's filtersValue.expanded would
    // persist the pre-click expansion, and the next drawer open would
    // re-init the tree's local path from that stale value (Settings
    // L73-77), rendering the wrong expansion. Omitting `expanded`
    // from the body lets setFilter's shallow-merge preserve whatever
    // expanded was previously in Redux — which matches PARITY-5's
    // documented behavior (tree won't auto-expand to the just-clicked
    // node on next open, but at least it doesn't actively regress).
    // The Apply path runs in a separate event tick after React has
    // re-rendered, so its `path` is current.
    if (opts.includeExpanded) body.expanded = path;
    // Apply closes the drawer (open: false); tree-click commit does not
    // — the user is mid-interaction, closing the drawer on every tree
    // click would interrupt workflow.
    if (opts.closeDrawer) body.open = false;
    return body;
  }

  function applyFn() {
    dispatch(setMapPage(1));
    dispatch(setListPage(1));
    dispatch(setFilter(buildFilterBody({ closeDrawer: true, includeExpanded: true })));
    props.toggleDrawer();
  }

  // Tree-click commit. Mirrors applyFn's filter-slice + pagination
  // dispatches with three intentional differences:
  //   - `group` is overridden to the just-clicked node (Settings'
  //     local `group` state lifts from Groups.handleSelect via
  //     props.setGroup, but that setState hasn't re-rendered yet at
  //     the moment Groups calls this callback — so we accept the new
  //     group explicitly to avoid a stale-render).
  //   - `expanded` is omitted (see buildFilterBody comment for the
  //     stale-closure rationale).
  //   - `closeDrawer` is false: drawer stays open so the user can
  //     continue interacting.
  function onTreeNodeCommit(nextGroup) {
    dispatch(setMapPage(1));
    dispatch(setListPage(1));
    dispatch(setFilter(buildFilterBody({ group: nextGroup, closeDrawer: false, includeExpanded: false })));
  }

  function resetFn() {
    dispatch(setMapPage(1));
    dispatch(setListPage(1));
    dispatch(resetFilter(service ? {...service, filtersValue} : {filtersValue}));
    setAlarms([]);
    setConnection("");
    filterForm.resetForm();
    props.toggleDrawer();
  }

  function filterComp() {
    return (
      <div style={{ marginBottom: "20px" }}>
        {service.assets.length > 1 ? 
          <>
            <span
              style={{
                display: "flex",
                gap: "20px",
                alignItems: "center",
              }}
            >
              <h4>
                <strong>{t("Asset Types")}:</strong>
              </h4>
              <div style={{display: "flex", width: "100%", overflowX: "scroll"}}>
                {service.assets.map((asset, i) => (
                  <div
                    className={styles.assetCard} 
                    key={i}
                    style={{
                      // borderColor: isSelected(asset.id) ? "rgb(121, 195, 124)" : "rgb(85,85,85)",
                      backgroundColor: isSelected(asset.id) ? "rgba(121, 195, 124, 0.1)" : "rgba(85,85,85,0.1)"
                    }}
                    onClick={() => {
                      if(assetTypes == null){
                        setAssetTypes([asset.id])
                      } else{
                        let newAssets = []
                        if(isSelected(asset.id)){
                          newAssets = assetTypes.filter((id) => id !== asset.id)
                        } else {
                          newAssets = [...assetTypes, asset.id]
                        }
                        setAssetTypes(newAssets)
                      }
                    }}
                  >
                    <img src={asset.image || Pin} height={75} width={80}/>
                    <span style={{
                      fontSize: "12px", 
                      paddingBlock: "3px", 
                      fontWeight: "bold",
                      color: isSelected(asset.id) ? "rgb(121, 195, 124)" : "rgb(85,85,85)"
                    }}>
                      {asset.name}
                    </span>
                  </div>
                ))}
              </div>
            </span>
            <Divider style={{ marginTop: "20px", marginBottom: "20px" }} /> 
          </>: null}          
        <span
          style={{
            display: "flex",
            gap: "20px",
            alignItems: "center",
          }}
        >
          <h4>
            <strong>{t("Alarms")}:</strong>
          </h4>
          {[
            { name: "CRITICAL", label: "CRITICAL", color: "#bf3535" },
            { name: "MAJOR", label: "MAJOR", color: "#844204" },
            { name: "MINOR", label: "MINOR", color: "#ffa11e " },
            { name: "WARNING", label: "INFO", color: "#3399ff" },
          ].map((elm) => (
            <Chip
              icon={
                <NotificationsActiveIcon
                  fontSize="small"
                  style={
                    alarms.indexOf(elm.name) != -1
                      ? {
                          color: "white",
                          marginLeft: "10px",
                        }
                      : {
                          marginLeft: "10px",
                        }
                  }
                />
              }
              onClick={() => {
                handleAlarms(elm.name);
              }}
              clickable
              label={
                <span
                  style={{
                    fontSize:
                      window.localStorage.getItem("Language") == "en"
                        ? "13px"
                        : "16px",
                    color: alarms.indexOf(elm.name) != -1 ? "white" : "#555555",
                  }}
                >
                  <b>{t(service.solutionLayout.dashboardView != 1 ? elm.name : elm.label)}</b>
                </span>
              }
              style={
                alarms.indexOf(elm.name) != -1
                  ? {
                      color: "white",
                      backgroundColor: elm.color,
                    }
                  : {}
              }
            />
          ))}
        </span>
        <Divider style={{ marginTop: "20px" }} />

        <span
          style={{
            display: "flex",
            marginTop: "20px",
            gap: "20px",
            alignItems: "center",
          }}
        >
          <h4>
            <strong>{t("Connectivity")}:</strong>
          </h4>
          {[
            { name: "AVAILABLE", color: "#5fb762" },
            { name: "UNAVAILABLE", color: "#555555" },
            { name: "NEVER", color: "rgb(186,117,216)" },
          ].map((elm) => (
            <Chip
              icon={
                <CompareArrowsIcon
                  fontSize="small"
                  style={
                    connection == elm.name
                      ? {
                          color: "white",
                          marginLeft: "10px",
                        }
                      : {
                          marginLeft: "10px",
                        }
                  }
                />
              }
              onClick={() => {
                handleConnection(elm.name);
              }}
              clickable
              label={
                <span
                  style={{
                    fontSize:
                      window.localStorage.getItem("Language") == "en"
                        ? "13px"
                        : "16px",
                    color: connection == elm.name ? "white" : "#555555",
                  }}
                >
                  <b>
                    {elm.name == "AVAILABLE"
                      ? t("ACTIVE")
                      : elm.name == "UNAVAILABLE"
                      ? t("INACTIVE")
                      : t("NO COMMUNICATION")}
                  </b>
                </span>
              }
              style={
                connection == elm.name
                  ? {
                      color: "white",
                      backgroundColor: elm.color,
                    }
                  : {}
              }
            />
          ))}
        </span>
        <form>
          <Divider style={{ marginTop: "20px" }} />
          <span
            style={{
              display: "flex",
              gap: "15px",
              marginTop: "20px",
              marginBottom: "20px",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <h4>
              <strong>{t("measurement")}:</strong>
            </h4>
            <div style={{ display: "flex", gap: "20px", width: "100%" }}>
              <FormControl margin="dense" fullWidth>
                <InputLabel color="secondary">{t("sensorData")}</InputLabel>
                <Select
                  color="secondary"
                  name="sensorData"
                  required
                  label={t("sensorData")}
                  onChange={filterForm.handleChange}
                  value={filterForm.values.sensorData}
                >
                  {props.sensors.map((elm) => {
                    return (
                      <MenuItem value={elm.name}>{elm.friendlyName}</MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <FormControl margin="dense" fullWidth>
                <InputLabel color="secondary">{t("operator")}</InputLabel>
                <Select
                  fullWidth
                  required
                  name="operator"
                  color="secondary"
                  label={t("operator")}
                  onChange={filterForm.handleChange}
                  value={filterForm.values.operator}
                >
                  <MenuItem value="$eq">{t("equal")}</MenuItem>
                  <MenuItem value="$gt">{t("greater")}</MenuItem>
                  <MenuItem value="$gte">{t("greaterEqual")}</MenuItem>
                  <MenuItem value="$lt">{t("less")}</MenuItem>
                  <MenuItem value="$lte">{t("lessEqual")}</MenuItem>
                </Select>
              </FormControl>
              <TextField
                color="secondary"
                id="condition"
                margin="dense"
                label={t("value")}
                type="number"
                fullWidth
                onChange={filterForm.handleChange}
                value={filterForm.values.condition}
              />
            </div>
            <HighlightOffIcon
              onClick={() => {
                filterForm.setFieldValue("sensorData", "");
                filterForm.setFieldValue("operator", "");
                filterForm.setFieldValue("condition", "");
              }}
              fontSize="small"
              style={{
                cursor: "pointer",
                color: "#e73e3a",
              }}
            />
          </span>

          {allMeta.length ? <Divider style={{ marginTop: "20px" }} /> : null}
          {allMeta.length ? (
            <span
              style={{
                display: "flex",
                gap: "15px",
                marginTop: "20px",
                marginBottom: "20px",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <h4>
                <strong>Custom Attributes:</strong>
              </h4>
              <div style={{ display: "flex", gap: "20px", width: "100%" }}>
                <FormControl margin="dense" fullWidth>
                  <InputLabel color="secondary">Attribute</InputLabel>
                  <Select
                    id="metaKey"
                    color="secondary"
                    name="metaKey"
                    required
                    label="Attribute"
                    onChange={filterForm.handleChange}
                    value={filterForm.values.metaKey}
                  >
                    {allMeta.map((elm) => {
                      return <MenuItem value={elm.key}>{elm.key}</MenuItem>;
                    })}
                  </Select>
                </FormControl>
                <div
                  style={{
                    marginTop: 20,
                    color: "grey",
                    fontWeight: "bold",
                    fontSize: 14,
                  }}
                >
                  CONTAINS
                </div>
                <TextField
                  id="metaValue"
                  color="secondary"
                  margin="dense"
                  label={t("value")}
                  type="text"
                  fullWidth
                  onChange={filterForm.handleChange}
                  value={filterForm.values.metaValue}
                />
              </div>
              <HighlightOffIcon
                onClick={() => {
                  filterForm.setFieldValue("metaKey", "");
                  filterForm.setFieldValue("metaValue", "");
                }}
                fontSize="small"
                style={{
                  cursor: "pointer",
                  color: "#e73e3a",
                }}
              />
            </span>
          ) : null}
        </form>
      </div>
    );
  }



  return (
    <Fragment>
      <div
        style={{
          position: "relative",
          height: "100vh",
          width: "700px",
          margin: "20px 20px 10px 20px",
          direction:
            window.localStorage.getItem("Language") == "ar" ? "rtl" : "ltr",
        }}
      >
        <Box sx={{ width: "100%", typography: "body1" }}>
          <TabContext value={filtersValue.view}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <TabList
                onChange={handleChange}
                aria-label="lab API tabs example"
              >
                <Tab disabled={props.tab == 2} label="Assets Filter(s)" value="1" />

                {metaDataValue.groupPermissions[props.group] != "DISABLE" ? (
                  <Tab disabled={props.tab == 0} label="Groups" value="2" />
                ) : null}
              </TabList>
            </Box>
            <TabPanel value="1">{filterComp()}</TabPanel>
            {metaDataValue.groupPermissions[props.group] != "DISABLE" ? (
              <TabPanel value="2">
                <Groups
                  id={props.id}
                  group={group}
                  setGroup={setGroup}
                  history={props.history}
                  refetch={true}
                  height={"calc(100vh - 175px)"}
                  path={path}
                  setPath={setPath}
                  serviceDashboard={props.serviceDashboard ? true : false}
                  // Tree-click commit: commit the FULL drawer state
                  // (alarms, connection, assetTypes, measurement,
                  // metaTags, plus the just-clicked group) to Redux on
                  // every user-originated tree-click. Two reasons:
                  //   (a) Without ANY commit, a user who clicks EST in
                  //       the tree then closes via backdrop/Escape
                  //       silently keeps the prior group (the original
                  //       Rensair cross-pollution bug).
                  //   (b) With group-only commit, a user who first
                  //       ticked CRITICAL alarm THEN clicked the tree
                  //       node THEN closed via backdrop would see the
                  //       group commit but the alarm chip silently
                  //       revert on next drawer open (PARITY-2).
                  // Routing through onTreeNodeCommit derives the same
                  // body shape as applyFn, so tree-click and Apply are
                  // semantically equivalent except the former leaves
                  // the drawer open for continued interaction. The
                  // shared <Groups> uses event!==null inside its
                  // handleSelect to skip this on synthetic mount-time
                  // re-selection (the auto-reselect path at L216-221
                  // of Groups/index.js).
                  onTreeNodeCommit={onTreeNodeCommit}
                />
              </TabPanel>
            ) : null}
          </TabContext>
        </Box>

        <div
          style={{
            position: "absolute",
            width: "100%",
            bottom: "0",
          }}
        >
          <Divider />
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginTop: "10px",
            }}
          >
            <Button onClick={resetFn} color="secondary">
              {t("reset")}
            </Button>
            <Button onClick={applyFn} color="secondary">
              {t("apply")}
            </Button>
          </div>
        </div>
      </div>
    </Fragment>
  );
}
